// RD-166: модель дерева подразделений для DepartmentsTree.
// Структура GET /api/departments/tree/ выводится полностью: уровни не скрываются и не добавляются.

export interface DepartmentTreeNode {
    id: string
    name: string
    children: DepartmentTreeNode[] | null
}

export interface DepartmentTreeEntry {
    // Путь из id от корня: уникален даже при повторе id в разных ветках.
    key: string
    node: DepartmentTreeNode
    parentKey: string | null
    depth: number
    childKeys: string[]
    // Голова — узел с потомками; выбирается целиком только в Аналитике.
    isGroup: boolean
    // Магазин — лист из списка доступных магазинов (GET /api/departments/me/?only_leafs=true).
    isStore: boolean
    // Число доступных магазинов поддерева; null — список магазинов неизвестен.
    storeCount: number | null
    // Адрес из уже загруженных данных Department по совпадающему id; в tree-ответе его нет.
    address: string
}

export interface DepartmentTreeModel {
    entries: Map<string, DepartmentTreeEntry>
    rootKeys: string[]
    // Все записи в порядке серверного дерева (обход в глубину).
    order: DepartmentTreeEntry[]
}

// Максимум шагов визуального отступа; реальная глубина не ограничена.
export const MAX_INDENT_LEVEL = 5

export function normalizeTreeRoots(raw: unknown): DepartmentTreeNode[] {
    if (Array.isArray(raw)) return raw as DepartmentTreeNode[]
    return raw && typeof raw === 'object' ? [raw as DepartmentTreeNode] : []
}

export function buildDepartmentTree(
    roots: DepartmentTreeNode[],
    storeIds: Set<string> | null,
    addresses: Map<string, string>,
): DepartmentTreeModel {
    const entries = new Map<string, DepartmentTreeEntry>()
    const order: DepartmentTreeEntry[] = []

    // Возвращает id доступных магазинов поддерева (Set — без двойного счёта повторов).
    const visit = (node: DepartmentTreeNode, parentKey: string | null, depth: number): Set<string> => {
        const key = parentKey ? `${parentKey}/${node.id}` : node.id
        const children = Array.isArray(node.children) ? node.children : []
        const entry: DepartmentTreeEntry = {
            key,
            node,
            parentKey,
            depth,
            childKeys: [],
            isGroup: children.length > 0,
            isStore: children.length === 0 && Boolean(storeIds?.has(node.id)),
            storeCount: null,
            address: addresses.get(node.id) ?? '',
        }
        entries.set(key, entry)
        order.push(entry)

        const stores = new Set<string>()
        if (entry.isStore) stores.add(node.id)
        for (const child of children) {
            const childStores = visit(child, key, depth + 1)
            entry.childKeys.push(`${key}/${child.id}`)
            childStores.forEach((id) => stores.add(id))
        }
        if (storeIds) entry.storeCount = stores.size
        return stores
    }

    const rootKeys: string[] = []
    for (const root of roots) {
        visit(root, null, 0)
        rootKeys.push(root.id)
    }
    return { entries, rootKeys, order }
}

export function ancestorKeys(model: DepartmentTreeModel, key: string): string[] {
    const result: string[] = []
    let parent = model.entries.get(key)?.parentKey ?? null
    while (parent) {
        result.push(parent)
        parent = model.entries.get(parent)?.parentKey ?? null
    }
    return result
}

export function findEntryById(model: DepartmentTreeModel, id: string | undefined | null) {
    if (!id) return undefined
    return model.order.find((entry) => entry.node.id === id)
}

// Видимые строки: раскрытые ветки; при поиске — только совпадения и их предки.
export function visibleEntries(
    model: DepartmentTreeModel,
    expanded: Set<string>,
    filter: Set<string> | null,
): DepartmentTreeEntry[] {
    const rows: DepartmentTreeEntry[] = []
    const walk = (keys: string[]) => {
        for (const key of keys) {
            const entry = model.entries.get(key)
            if (!entry || (filter && !filter.has(key))) continue
            rows.push(entry)
            if (entry.isGroup && expanded.has(key)) walk(entry.childKeys)
        }
    }
    walk(model.rootKeys)
    return rows
}

export function normalizeQuery(value: string) {
    return value.trim().toLocaleLowerCase('ru')
}

// Поиск без учёта регистра по названию и адресу; код отдельными данными не приходит.
export function searchTree(model: DepartmentTreeModel, query: string) {
    const matches = new Set<string>()
    const ancestors = new Set<string>()
    for (const entry of model.order) {
        const name = entry.node.name?.toLocaleLowerCase('ru') ?? ''
        const address = entry.address.toLocaleLowerCase('ru')
        if (name.includes(query) || address.includes(query)) {
            matches.add(entry.key)
            ancestorKeys(model, entry.key).forEach((key) => ancestors.add(key))
        }
    }
    const visible = new Set<string>([...matches, ...ancestors])
    return { matches, ancestors, visible }
}

export interface TextPart {
    text: string
    match: boolean
}

export function splitByQuery(text: string, query: string): TextPart[] {
    if (!query || !text) return [{ text, match: false }]
    const lower = text.toLocaleLowerCase('ru')
    const parts: TextPart[] = []
    let from = 0
    let index = lower.indexOf(query, from)
    while (index !== -1) {
        if (index > from) parts.push({ text: text.slice(from, index), match: false })
        parts.push({ text: text.slice(index, index + query.length), match: true })
        from = index + query.length
        index = lower.indexOf(query, from)
    }
    if (from < text.length) parts.push({ text: text.slice(from), match: false })
    return parts
}
